/**
 * Sync Service
 * 
 * Manages real-time collaboration between IndexedDB (source of truth) and MongoDB
 * Features:
 * - Periodic sync of IndexedDB state to MongoDB (10-15s)
 * - Real-time WebSocket events for file changes, cursors, user presence
 * - Merge remote changes into IndexedDB (last-write-wins)
 * - Offline change queueing and retry
 * - Cursor position tracking with throttling (150ms)
 * - Project-level collaboration with invite tokens
 */

import config from '../config';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const SYNC_INTERVAL = 12000; // 12 seconds
const CURSOR_THROTTLE = 150; // 150ms cursor throttle
const OFFLINE_RETRY_INTERVAL = 30000; // 30 seconds for offline retry
const RECONNECT_DELAY = 3000; // 3 seconds
const MAX_RECONNECT_ATTEMPTS = 2; // Only try twice to avoid console spam

class SyncService {
  constructor() {
    this.ws = null;
    this.userId = null;
    this.projectId = null;
    this.username = null;
    this.sessionId = null;
    this.connectionId = Math.random().toString(36).substring(7); // Unique per browser tab
    this.userColor = '#' + Math.floor(Math.random()*16777215).toString(16);
    this.currentFile = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Listeners for UI updates
    this.listeners = {
      fileUpdated: new Set(),
      cursorMoved: new Set(),
      userJoined: new Set(),
      userLeft: new Set(),
      snapshotSaved: new Set(),
      connectionStatus: new Set(),
      collaboratorsChanged: new Set(),
    };
    
    // Sync state
    this.syncInterval = null;
    this.lastSyncTimestamp = null;
    this.offlineQueue = [];
    this.isOnline = navigator.onLine;
    this.isSyncing = false;
    
    // Cursor throttling
    this.cursorThrottle = null;
    this.lastCursorUpdate = 0;
    
    // Collaborators cache
    this.collaborators = new Map(); // userId -> {username, color, cursor}
    
    // Track if we're initialized
    this._initialized = false;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnlineStatusChange(true));
    window.addEventListener('offline', () => this.handleOnlineStatusChange(false));
  }

  /**
   * Initialize sync service for a project
   * @param {string} userId - Current user ID
   * @param {string} projectId - Project ID to sync
   * @param {string} username - Display username
   */
  async initialize(userId, projectId, username) {
    if (this._initialized && this.projectId === projectId && this.userId === userId) {
      console.log('[Sync] Already initialized for this project');
      return;
    }

    // Disconnect previous session if exists
    if (this._initialized) {
      await this.disconnect();
    }

    this.userId = userId;
    this.projectId = projectId;
    this.sessionId = projectId; // Use projectId as sessionId
    this.username = username;
    
    console.log(`[Sync] Initializing sync service for project ${projectId}, user ${userId}`);
    
    // Connect WebSocket
    await this.connectWebSocket();
    
    // Start periodic sync
    this.startPeriodicSync();
    
    // Initial sync to server
    await this.syncToServer();
    
    this._initialized = true;
    this.notifyListeners('connectionStatus', { status: 'connected' });
  }

  /**
   * Check if sync service is ready
   * @returns {boolean} - True if initialized with valid project/user
   */
  isInitialized() {
    return !!(this.projectId && this.userId && this._initialized);
  }

  /**
   * Check if BFF supports sync endpoints
   */
  async checkSyncEndpoint() {
    try {
      // Use projectId as sessionId (consistent with our approach)
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/projects/${this.projectId}/collaborators?sessionId=${this.projectId}`, {
        method: 'GET',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
      });
      return response.ok || response.status === 404; // 404 means endpoint exists but no data
    } catch (error) {
      console.warn('[Sync] Failed to check sync endpoint:', error.message);
      return false;
    }
  }

  /**
   * Connect to WebSocket for real-time events
   */
  async connectWebSocket() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[Sync] WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('[Sync] WebSocket connection already in progress');
      return;
    }

    this.isConnecting = true;

    try {
      // Check if BFF supports WebSocket sync endpoint
      const bffSupportsSync = await this.checkSyncEndpoint();
      if (!bffSupportsSync) {
        console.warn('[Sync] BFF does not support /ws/sync endpoint yet. Collaboration features disabled.');
        this.isConnecting = false;
        this.notifyListeners('connectionStatus', { status: 'unavailable' });
        return;
      }

      // Convert HTTP to WebSocket URL
      const wsUrl = config.bffUrl
        .replace('http://', 'ws://')
        .replace('https://', 'wss://');

      const url = `${wsUrl}/ws/sync?userId=${encodeURIComponent(this.userId)}&projectId=${encodeURIComponent(this.projectId)}&username=${encodeURIComponent(this.username)}`;
      
      console.log('[Sync] Connecting to WebSocket:', url);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[Sync] WebSocket connected');
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.notifyListeners('connectionStatus', { status: 'connected' });
        
        // Process offline queue
        this.processOfflineQueue();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleWebSocketMessage(message);
        } catch (error) {
          console.error('[Sync] Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        // Suppress error if we're expecting it to fail (during reconnect attempts)
        if (this.reconnectAttempts === 0) {
          console.warn('[Sync] WebSocket connection failed (endpoint may not be available)');
        }
        this.isConnecting = false;
      };

      this.ws.onclose = (event) => {
        // Only log close events on first attempt
        if (this.reconnectAttempts === 0) {
          console.log('[Sync] WebSocket closed:', event.code);
        }
        this.isConnecting = false;
        this.ws = null;
        
        this.notifyListeners('connectionStatus', { status: 'disconnected' });

        // Attempt to reconnect
        if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS && this.isInitialized) {
          this.reconnectAttempts++;
          
          setTimeout(() => {
            if (this.isInitialized) {
              this.connectWebSocket();
            }
          }, RECONNECT_DELAY);
        } else if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.info('[Sync] ℹ️ Real-time collaboration unavailable - WebSocket endpoint not configured in BFF');
          this.notifyListeners('connectionStatus', { status: 'unavailable' });
        }
      };
    } catch (error) {
      console.error('[Sync] Failed to create WebSocket:', error);
      this.isConnecting = false;
      throw error;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleWebSocketMessage(message) {
    // Backend sends flat objects: { type: 'cursorMoved', userId, username, ... }
    const type = message.type;
    
    console.log('[Sync] WebSocket message:', type, message);

    switch (type) {
      case 'fileUpdated':
      case 'file:updated':
        this.handleRemoteFileUpdate(message);
        break;
        
      case 'cursorMoved':
      case 'cursor:moved':
        this.handleRemoteCursorUpdate(message);
        break;
        
      case 'userJoined':
      case 'user:joined':
        this.handleUserJoined(message);
        break;
        
      case 'userLeft':
      case 'user:left':
        this.handleUserLeft(message);
        break;
        
      case 'snapshot:saved':
        this.handleSnapshotSaved(message);
        break;

      case 'pong':
        // Keep-alive response
        break;
        
      default:
        console.warn('[Sync] Unknown message type:', type);
    }
  }

  /**
   * Handle remote file update - merge into IndexedDB
   */
  async handleRemoteFileUpdate(data) {
    const { userId, connectionId, filePath, content, timestamp, serviceName } = data;
    
    console.log('[Sync] handleRemoteFileUpdate called:', { userId, myUserId: this.userId, connectionId, myConnectionId: this.connectionId, filePath, serviceName, hasContent: !!content, contentLength: content?.length });
    
    // Ignore updates from our own connection (same tab), but allow updates from same user in different tabs
    if (connectionId && connectionId === this.connectionId) {
      console.log('[Sync] Ignoring update from same connection/tab');
      return;
    }

    try {
      console.log(`[Sync] Processing remote file update for ${filePath} from user ${userId}`);
      
      // Get current file from IndexedDB
      const currentFile = await indexedDbFileStorage.getFile(this.projectId, serviceName, filePath);
      
      // Last-write-wins: only update if remote is newer
      const remoteTimestamp = new Date(timestamp).getTime();
      const localTimestamp = currentFile?.lastModified || 0;
      
      console.log('[Sync] Timestamp comparison:', { remoteTimestamp, localTimestamp, willUpdate: !currentFile || remoteTimestamp > localTimestamp });
      
      if (!currentFile || remoteTimestamp > localTimestamp) {
        console.log(`[Sync] Applying remote update for ${filePath} from user ${userId}`);
        
        await indexedDbFileStorage.saveFile(this.projectId, serviceName, filePath, content, {
          branch: currentFile?.branch,
          originalContent: currentFile?.originalContent,
        });
        
        // Notify listeners
        console.log('[Sync] Notifying fileUpdated listeners');
        this.notifyListeners('fileUpdated', {
          filePath,
          serviceName,
          content,
          userId,
          username: this.collaborators.get(userId)?.username || 'Unknown',
        });
      } else {
        console.log(`[Sync] Skipping older remote update for ${filePath}`);
      }
    } catch (error) {
      console.error('[Sync] Failed to handle remote file update:', error);
    }
  }

  /**
   * Handle remote cursor update
   */
  handleRemoteCursorUpdate(data) {
    const { userId, username, position, filePath, serviceName, color, connectionId } = data;
    
    console.log('[Sync] Remote cursor update:', { userId, username, position, filePath, serviceName, color, connectionId, myConnectionId: this.connectionId });
    
    // Ignore our own cursor by connectionId (not userId, since same user can have multiple tabs)
    if (connectionId && connectionId === this.connectionId) {
      console.log('[Sync] Ignoring own cursor update (same connectionId)');
      return;
    }

    // Update collaborator cache
    const collaborator = this.collaborators.get(userId) || {};
    this.collaborators.set(userId, {
      ...collaborator,
      username,
      color,
      cursor: { position, filePath, serviceName, timestamp: Date.now() },
    });

    // Notify listeners
    console.log('[Sync] Notifying cursorMoved listeners');
    this.notifyListeners('cursorMoved', {
      userId,
      username,
      position,
      filePath,
      serviceName,
      color,
    });
  }

  /**
   * Handle user joined event
   */
  handleUserJoined(data) {
    const { userId, username, color } = data;
    
    console.log(`[Sync] User joined: ${username} (${userId})`);
    
    this.collaborators.set(userId, {
      username,
      color,
      cursor: null,
    });

    this.notifyListeners('userJoined', { userId, username, color });
    this.notifyListeners('collaboratorsChanged', { collaborators: Array.from(this.collaborators.values()) });
  }

  /**
   * Handle user left event
   */
  handleUserLeft(data) {
    const { userId, username } = data;
    
    console.log(`[Sync] User left: ${username} (${userId})`);
    
    this.collaborators.delete(userId);

    this.notifyListeners('userLeft', { userId, username });
    this.notifyListeners('collaboratorsChanged', { collaborators: Array.from(this.collaborators.values()) });
  }

  /**
   * Handle snapshot saved event
   */
  handleSnapshotSaved(data) {
    console.log('[Sync] Snapshot saved:', data);
    this.notifyListeners('snapshotSaved', data);
  }

  /**
   * Start periodic sync to server
   */
  startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      await this.syncToServer();
    }, SYNC_INTERVAL);

    console.log('[Sync] Periodic sync started (interval:', SYNC_INTERVAL, 'ms)');
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('[Sync] Periodic sync stopped');
    }
  }

  /**
   * Sync IndexedDB state to MongoDB server
   */
  async syncToServer() {
    if (this.isSyncing) {
      console.log('[Sync] Sync already in progress, skipping');
      return;
    }

    if (!this.isOnline) {
      console.log('[Sync] Offline, queueing sync for later');
      return;
    }

    this.isSyncing = true;

    try {
      // Get all modified files from IndexedDB
      // Note: getAllFiles requires serviceName, but we want project-level sync
      // For now, skip file sync if we don't have proper context
      if (!this.projectId) {
        console.log('[Sync] No project context for sync');
        this.isSyncing = false;
        return;
      }

      // TODO: Implement project-level file enumeration
      // For now, we skip file sync and only handle cursor/presence
      // console.log('[Sync] File sync skipped - requires service context');
      this.isSyncing = false;
      return;
      
      // const changedFiles = await indexedDbFileStorage.getAllFiles(this.projectId);
      // 
      // if (!changedFiles || changedFiles.length === 0) {
      //   console.log('[Sync] No files to sync');
      //   this.isSyncing = false;
      //   return;
      // }

      // Prepare files for sync
      const filesToSync = changedFiles.map(file => ({
        filePath: file.filePath,
        content: file.content,
        serviceName: file.serviceName,
        timestamp: file.lastModified,
        state: file.state,
      }));

      console.log(`[Sync] Syncing ${filesToSync.length} files to server`);

      // Send to server
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/projects/${this.projectId}/files`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          userId: this.userId,
          files: filesToSync,
        }),
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Sync] Sync completed:', result);

      this.lastSyncTimestamp = Date.now();
      
    } catch (error) {
      console.error('[Sync] Sync to server failed:', error);
      
      // Queue for retry if offline
      if (!this.isOnline) {
        this.queueOfflineOperation({ type: 'sync', timestamp: Date.now() });
      }
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Update cursor position (throttled)
   * @param {string} filePath - Current file path
   * @param {string} serviceName - Service name
   * @param {object} position - Cursor position {line, column}
   */
  updateCursor(filePath, serviceName, position) {
    const now = Date.now();
    
    // Throttle cursor updates
    if (now - this.lastCursorUpdate < CURSOR_THROTTLE) {
      // Queue the update
      if (this.cursorThrottle) {
        clearTimeout(this.cursorThrottle);
      }
      
      this.cursorThrottle = setTimeout(() => {
        this.sendCursorUpdate(filePath, serviceName, position);
      }, CURSOR_THROTTLE);
      
      return;
    }

    this.sendCursorUpdate(filePath, serviceName, position);
  }

  /**
   * Send cursor update to server
   */
  async sendCursorUpdate(filePath, serviceName, position) {
    this.lastCursorUpdate = Date.now();
    this.currentFile = { filePath, serviceName };

    if (!this.isOnline || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Send via WebSocket
    try {
      this.ws.send(JSON.stringify({
        type: 'cursorUpdate',
        filePath,
        serviceName,
        position,
        color: this.userColor,
        connectionId: this.connectionId,
      }));
    } catch (error) {
      console.error('[Sync] Failed to send cursor update:', error);
    }
  }

  /**
   * Send file update to server (broadcast content changes to collaborators)
   */
  async sendFileUpdate(filePath, serviceName, content) {
    console.log('[Sync] sendFileUpdate called:', { filePath, serviceName, contentLength: content?.length });
    
    if (!this.isOnline || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('[Sync] Cannot send file update - not connected');
      return;
    }

    // Send via WebSocket
    try {
      const message = {
        type: 'fileChanged',
        filePath,
        serviceName,
        content,
        connectionId: this.connectionId, // Include connectionId to distinguish tabs
        timestamp: Date.now(),
      };
      
      console.log('[Sync] Sending WebSocket message:', { 
        type: message.type, 
        filePath: message.filePath, 
        serviceName: message.serviceName,
        contentLength: message.content?.length,
        timestamp: message.timestamp
      });
      
      this.ws.send(JSON.stringify(message));
      console.log('[Sync] File update sent:', { filePath, serviceName });
    } catch (error) {
      console.error('[Sync] Failed to send file update:', error);
    }
  }

  /**
   * Generate invite token for project collaboration
   * @param {number} expiresInHours - Token expiry in hours (default 24)
   */
  async generateInviteToken(expiresInHours = 24) {
    try {
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/projects/${this.projectId}/collaborators`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          sessionId: this.projectId, // Use projectId as sessionId for simplicity
          ownerId: this.userId,
          projectName: this.projectId, // Use projectId as name for now (could be enhanced later)
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate invite: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Sync] Invite token generated:', result);
      
      // Always generate invite link from frontend origin (not backend's inviteUrl)
      const token = result.data?.inviteToken || result.inviteToken;
      
      return {
        token,
        expiresAt: result.data?.inviteTokenExpiresAt || result.expiresAt,
        inviteLink: `${window.location.origin}/join/${token}`,
      };
    } catch (error) {
      console.error('[Sync] Failed to generate invite token:', error);
      throw error;
    }
  }

  /**
   * Join project by invite token
   * @param {string} token - Invite token
   * @param {string} userId - User ID (optional if service is initialized)
   * @param {string} username - Username (optional if service is initialized)
   */
  async joinByToken(token, userId = null, username = null) {
    try {
      const finalUserId = userId || this.userId;
      const finalUsername = username || this.username;

      if (!finalUserId) {
        throw new Error('User ID is required to join project');
      }

      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/join/${token}`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          userId: finalUserId,
          username: finalUsername || 'Anonymous',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to join project');
      }

      const result = await response.json();
      console.log('[Sync] Joined project:', result);
      
      return result;
    } catch (error) {
      console.error('[Sync] Failed to join by token:', error);
      throw error;
    }
  }

  /**
   * Get project collaborators
   */
  async getCollaborators() {
    if (!this.isInitialized()) {
      console.warn('[Sync] Cannot get collaborators - service not initialized');
      return { success: false, data: { activeConnections: [] } };
    }

    try {
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/projects/${encodeURIComponent(this.projectId)}/collaborators?sessionId=${encodeURIComponent(this.sessionId)}`, {
        method: 'GET',
        headers: getBffHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get collaborators: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Sync] Collaborators:', result);
      
      return result;
    } catch (error) {
      console.error('[Sync] Failed to get collaborators:', error);
      throw error;
    }
  }

  /**
   * Save snapshot of current state
   */
  async saveSnapshot(description) {
    try {
      const files = await indexedDbFileStorage.getAllFiles(this.projectId);
      
      const response = await getAuthorizedFetch()(`${config.bffUrl}/api/sync/projects/${this.projectId}/snapshot`, {
        method: 'POST',
        headers: getBffHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          userId: this.userId,
          description,
          files: files.map(f => ({
            filePath: f.filePath,
            content: f.content,
            serviceName: f.serviceName,
            timestamp: f.lastModified,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save snapshot: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Sync] Snapshot saved:', result);
      
      return result;
    } catch (error) {
      console.error('[Sync] Failed to save snapshot:', error);
      throw error;
    }
  }

  /**
   * Handle online/offline status change
   */
  handleOnlineStatusChange(isOnline) {
    console.log('[Sync] Online status changed:', isOnline);
    this.isOnline = isOnline;
    
    this.notifyListeners('connectionStatus', { 
      status: isOnline ? 'online' : 'offline' 
    });

    if (isOnline) {
      // Reconnect WebSocket
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.connectWebSocket();
      }
      
      // Process offline queue
      this.processOfflineQueue();
      
      // Sync to server
      this.syncToServer();
    }
  }

  /**
   * Queue operation for offline retry
   */
  queueOfflineOperation(operation) {
    this.offlineQueue.push(operation);
    console.log('[Sync] Queued offline operation:', operation.type, 'Queue size:', this.offlineQueue.length);
  }

  /**
   * Process offline queue when back online
   */
  async processOfflineQueue() {
    if (this.offlineQueue.length === 0) {
      return;
    }

    console.log('[Sync] Processing offline queue, size:', this.offlineQueue.length);

    const queue = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const operation of queue) {
      try {
        if (operation.type === 'sync') {
          await this.syncToServer();
        }
      } catch (error) {
        console.error('[Sync] Failed to process offline operation:', error);
        // Re-queue if still offline
        if (!this.isOnline) {
          this.offlineQueue.push(operation);
        }
      }
    }
  }

  /**
   * Subscribe to sync events
   * @param {string} eventType - Event type (fileUpdated, cursorMoved, userJoined, userLeft, snapshotSaved, connectionStatus, collaboratorsChanged)
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(eventType, callback) {
    if (!this.listeners[eventType]) {
      console.warn(`[Sync] Unknown event type: ${eventType}`);
      return () => {};
    }

    this.listeners[eventType].add(callback);
    
    return () => {
      this.listeners[eventType].delete(callback);
    };
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(eventType, data) {
    if (!this.listeners[eventType]) {
      return;
    }

    this.listeners[eventType].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[Sync] Error in ${eventType} listener:`, error);
      }
    });
  }

  /**
   * Disconnect and cleanup
   */
  async disconnect() {
    console.log('[Sync] Disconnecting sync service');
    
    this._initialized = false;
    this.stopPeriodicSync();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    
    if (this.cursorThrottle) {
      clearTimeout(this.cursorThrottle);
      this.cursorThrottle = null;
    }
    
    this.collaborators.clear();
    this.offlineQueue = [];
    
    this.notifyListeners('connectionStatus', { status: 'disconnected' });
  }

  /**
   * Get current collaborators
   */
  getCurrentCollaborators() {
    return Array.from(this.collaborators.entries()).map(([userId, data]) => ({
      userId,
      ...data,
    }));
  }

  /**
   * Check if user is collaborating
   */
  isCollaborating(userId) {
    return this.collaborators.has(userId);
  }
}

// Export singleton instance
const syncService = new SyncService();
export default syncService;
